import '.d{display:flex;}.ssr-plugin'
import '.aq{gap:0.5rem;}.ssr-plugin'
import '.v{align-items:center;}.ssr-plugin'
import '.i{position:fixed;}.ssr-plugin'
import '.fq{min-width:12rem;}.ssr-plugin'
import '.ad{background-color:var(--palette-white);}.ssr-plugin'
import '.cn{border-radius:10px;}.ssr-plugin'
import '.fr{border:1px solid var(--gray-100);}.ssr-plugin'
import '.fs{box-shadow:0 3px 10px 0 rgba(0, 0, 0, 0.05);}.ssr-plugin'
import '.cu{z-index:10;}.ssr-plugin'
import '.ae{overflow:hidden;}.ssr-plugin'
import '.ft{padding:0.75rem 1.25rem;}.ssr-plugin'
import '.aw{cursor:pointer;}.ssr-plugin'
import '.cp{justify-content:flex-start;}.ssr-plugin'
import { useState, useRef, useEffect } from 'react'
import { styled, type Style } from 'inlines'
import { Text } from '../../typography/Text'
import useClickOutside from '@app/util/hooks/useClickOutside'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faChevronDown } from '@fortawesome/pro-solid-svg-icons'

export type MoreDropdownTabProps<T extends string> = {
  label: string
  dropdownKeys: T[]
  selectedTab: T
  onSelectTab: (key: T) => void
  getLabel: (key: T) => string
  tabItemStyle?: Style
}

export const MoreDropdownTab = <T extends string>({
  label,
  dropdownKeys,
  selectedTab,
  onSelectTab,
  getLabel,
  tabItemStyle,
}: MoreDropdownTabProps<T>) => {
  const [isOpen, setIsOpen] = useState(false)
  const dropdownRef = useRef<HTMLButtonElement>(null)
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 })

  const isAnyDropdownTabSelected = dropdownKeys.includes(selectedTab)

  useEffect(() => {
    if (!isOpen || typeof window === 'undefined') return
    const updatePosition = () => {
      if (dropdownRef.current) {
        const rect = dropdownRef.current.getBoundingClientRect()
        setDropdownPosition({ top: rect.bottom + 8, left: rect.left })
      }
    }
    updatePosition()
    window.addEventListener('scroll', updatePosition, true)
    window.addEventListener('resize', updatePosition)
    return () => {
      window.removeEventListener('scroll', updatePosition, true)
      window.removeEventListener('resize', updatePosition)
    }
  }, [isOpen])

  useClickOutside(dropdownRef, () => setIsOpen(false))

  const handleTabSelect = (key: T) => {
    onSelectTab(key)
    setIsOpen(false)
  }

  if (dropdownKeys.length === 0) return null

  const tabStyle: Style = {
    padding: '1.5rem',
    justifyContent: 'center',
    alignItems: 'center',
    display: 'flex',
    cursor: 'pointer',
    position: 'relative',
    background: 'none',
    border: 'none',
    overflow: 'visible',
    '&:hover': { color: 'var(--brand-secondary)' },
    borderBottom: `2px ${
      isAnyDropdownTabSelected ? 'var(--brand-secondary)' : 'transparent'
    } solid`,
  }

  const textStyle: Style = {
    wordWrap: 'break-word',
    color: isAnyDropdownTabSelected
      ? 'var(--brand-secondary)'
      : 'var(--palette-blue-300)',
    '&:hover': { color: 'var(--brand-secondary)' },
  }

  return (
    <styled.button
      ref={dropdownRef}
      style={{ ...tabStyle, ...tabItemStyle }}
      onClick={() => setIsOpen((p) => !p)}
      type="button"
    >
      <div className="d aq v"
      >
        <Text.Medium style={textStyle}>{label}</Text.Medium>
        <FontAwesomeIcon
          icon={faChevronDown}
          style={{
            color: isAnyDropdownTabSelected
              ? 'var(--brand-secondary)'
              : 'var(--palette-blue-300)',
            fontSize: '0.875rem',
            transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
            transition: 'transform 0.2s ease',
          }}
        />
      </div>
      {isOpen && (
        <div className="i fq ad cn fr fs cu ae"
          style={{
            top: `${dropdownPosition.top}px`,
            left: `${dropdownPosition.left}px`,}}
        >
          {dropdownKeys.map((key, index) => (
            <styled.div className="ft aw d cp v"
              key={key}
              onClick={(e) => {
                e.stopPropagation()
                handleTabSelect(key)
              }}
              style={{
                backgroundColor:
                  key === selectedTab
                    ? 'var(--gray-100)'
                    : 'var(--palette-white)',
                borderBottom:
                  index < dropdownKeys.length - 1
                    ? '1px solid var(--gray-100)'
                    : 'none',
                '&:hover': { backgroundColor: 'var(--gray-100)' },
              }}
            >
              <Text.Medium
                style={{ color: 'var(--brand-secondary)', fontSize: '1rem' }}
              >
                {getLabel(key)}
              </Text.Medium>
            </styled.div>
          ))}
        </div>
      )}
    </styled.button>
  )
}
